// src/pages/CohortAnalytics.tsx
import React from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import { db } from "../firebase";
import { ref, get, child } from "firebase/database";

type Student = { uid: string; name?: string; email?: string };
type Group = {
  id: string;
  name: string;
  createdAt?: string;
  members?: Record<string, true>;
  assignedProcedures?: Record<string, true>;
  procedures?: Record<string, true>; // legacy
};

type Attempt = {
  ts?: number | string;
  score?: number;
  procId?: string;
  name?: string;
};

function toList<T>(node: any): T[] {
  if (!node) return [];
  if (Array.isArray(node)) return node as T[];
  return Object.values(node) as T[];
}

function lastTs(list: Attempt[]): number | undefined {
  const v = list
    .map((a) =>
      typeof a.ts === "number"
        ? a.ts
        : typeof a.ts === "string"
        ? Date.parse(a.ts)
        : 0
    )
    .reduce((m, v) => (v > m ? v : m), 0);
  return v || undefined;
}

export default function CohortAnalytics() {
  const { groupId = "" } = useParams();
  const loc = useLocation() as any;
  const college: string =
    loc?.state?.college ||
    (sessionStorage.getItem("college") ?? "UNKNOWN_COLLEGE");

  const [loading, setLoading] = React.useState(true);
  const [group, setGroup] = React.useState<Group | null>(null);
  const [students, setStudents] = React.useState<Record<string, Student>>({});
  const [rows, setRows] = React.useState<
    {
      uid: string;
      name: string;
      email: string;
      started: boolean;
      coverage: number;
      attempts: number;
      last?: number;
    }[]
  >([]);

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      const root = ref(db);

      // group
      const gSnap = await get(
        child(root, `Colleges/${college}/Groups/${groupId}`)
      );
      if (!gSnap.exists()) {
        setGroup(null);
        setRows([]);
        setLoading(false);
        return;
      }
      const gVal = gSnap.val() || {};
      const g: Group = {
        id: groupId,
        name: gVal.name || "(untitled)",
        createdAt: gVal.createdAt,
        members: gVal.members || {},
        assignedProcedures: gVal.assignedProcedures || null,
        procedures: gVal.procedures || null,
      };
      setGroup(g);

      const memberUids = Object.keys(g.members || {});
      if (!memberUids.length) {
        setStudents({});
        setRows([]);
        setLoading(false);
        return;
      }

      // fetch students profile for display
      const usersSnap = await get(child(root, `Colleges/${college}/Users`));
      const users = (usersSnap.val() || {}) as Record<string, any>;
      const map: Record<string, Student> = {};
      memberUids.forEach((uid) => {
        const pd = users[uid]?.["Personal Data"] || {};
        map[uid] = { uid, name: pd.userName || uid, email: pd.userEmail || "" };
      });
      setStudents(map);

      // compute analytics per student
      const assignedIds = Object.keys(g.assignedProcedures || {});
      const legacyNames = Object.keys(g.procedures || {});
      const needIds = new Set(assignedIds);
      const needNames = new Set(legacyNames.map((s) => s.trim().toLowerCase()));

      const computed: {
        uid: string;
        name: string;
        email: string;
        started: boolean;
        coverage: number; // 0..100
        attempts: number;
        last?: number;
      }[] = [];

      for (const uid of memberUids) {
        const gmSnap = await get(
          child(root, `Colleges/${college}/Users/${uid}/Game Modes`)
        );
        const gm = gmSnap.val() || {};

        // gather attempts across shapes
        const attempts: Attempt[] = [];
        // a) node "Clinical Procedures"
        const cpNode =
          gm["Clinical Procedures"] || gm["ClinicalProcedures"] || null;
        if (cpNode && typeof cpNode === "object") {
          Object.keys(cpNode).forEach((key) => {
            const arr = toList<Attempt>(cpNode[key]);
            arr.forEach((a) => attempts.push({ ...a, procId: key }));
          });
        }
        // b) generic keys containing procId/name (fallback)
        Object.keys(gm || {}).forEach((k) => {
          const arr = toList<Attempt>(gm[k]);
          arr.forEach((a) => {
            if (a && (a.procId || a.name)) attempts.push(a);
          });
        });

        // coverage across assigned IDs / legacy names
        let hitIds = 0;
        let hitNames = 0;
        const seenIds = new Set<string>();
        const seenNames = new Set<string>();

        attempts.forEach((a) => {
          if (a.procId && needIds.has(a.procId)) seenIds.add(a.procId);
          if (a.name && needNames.has(a.name.trim().toLowerCase()))
            seenNames.add(a.name.trim().toLowerCase());
        });

        hitIds = seenIds.size;
        hitNames = seenNames.size;

        // choose denominator: prefer assigned IDs; else legacy names
        const denom = assignedIds.length
          ? assignedIds.length
          : legacyNames.length || 1;
        const coverage = Math.round(
          ((assignedIds.length ? hitIds : hitNames) / denom) * 100
        );

        const last = lastTs(attempts);
        const started = (assignedIds.length ? hitIds : hitNames) > 0;
        computed.push({
          uid,
          name: map[uid]?.name || uid,
          email: map[uid]?.email || "",
          attempts: attempts.length,
          coverage,
          started,
          last,
        });
      }

      // sort by started desc, then coverage desc, then last desc
      computed.sort((a, b) => {
        if (a.started !== b.started) return a.started ? -1 : 1;
        if (b.coverage !== a.coverage) return b.coverage - a.coverage;
        return (b.last || 0) - (a.last || 0);
      });

      setRows(computed);
      setLoading(false);
    })();
  }, [college, groupId]);

  return (
    <div className="container">
      <div className="row" style={{ marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Cohort Analytics</h2>
        <Link className="btn" to="/f/groups" state={{ college }}>
          Back
        </Link>
      </div>

      {!group ? (
        <div className="card">
          <p className="small">Cohort not found.</p>
        </div>
      ) : (
        <>
          <div className="card">
            <h3 style={{ marginTop: 0 }}>{group.name}</h3>
            <p className="small">Created: {group.createdAt || "—"}</p>
            <p className="small">
              Assigned procedures:{" "}
              {Object.keys(group.assignedProcedures || group.procedures || {})
                .length || 0}
            </p>
            <p className="small">
              Members: {Object.keys(group.members || {}).length || 0}
            </p>
          </div>

          <div className="card" style={{ marginTop: 12 }}>
            <h3 style={{ marginTop: 0 }}>Students</h3>
            {loading ? (
              <p>Loading…</p>
            ) : rows.length === 0 ? (
              <p className="small">No members.</p>
            ) : (
              <table className="table" style={{ marginTop: 6 }}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Started</th>
                    <th>Coverage</th>
                    <th>Attempts</th>
                    <th>Last activity</th>
                    <th>Open</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.uid}>
                      <td>{r.name}</td>
                      <td>{r.email}</td>
                      <td>{r.started ? "Yes" : "No"}</td>
                      <td>{r.coverage}%</td>
                      <td>{r.attempts}</td>
                      <td>
                        {r.last ? new Date(r.last).toLocaleString() : "—"}
                      </td>
                      <td>
                        <Link
                          className="btn"
                          to={`/student/${r.uid}`}
                          state={{ college }}
                        >
                          View dashboard
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
